import type { BibleBookmark } from '@/types/bookmark';
import { normalizeForSearch } from '@/lib/search-normalize';

// Verse-centric grouping for the bookmark thread redesign
// (docs/plans/bible-bookmarks-thread-redesign-2026-06-01.md §3). Pure
// helpers, normal `@/` imports (NOT probe-importable; no probe consumer).
// A schema-free display projection over existing BibleBookmark records:
// the same verse can be covered by several notes (a single-verse note
// and a passage note that includes it), and the thread shows all of
// them, each labeled by its own passage reference, so co-terminal or
// overlapping passages are never ambiguous.

/**
 * Note count per verse for this chapter, from live (non-deleted)
 * bookmarks. `Map.has(v)` answers "is verse v bookmarked" (drives the
 * tint + which verse runs are tappable-to-open); `Map.get(v)` is the
 * count for the verse-run aria-label. The reader computes this in a
 * `bookSlug`-guarded `useMemo`, so `renderVerse` reads the Map and never
 * touches the optional `bookSlug` (Revision 3 BLOCKER 2). Legacy
 * whole-chapter rows (`verses: []`) cover no verse, so they contribute
 * nothing here (D5).
 */
export function bookmarkCountsByVerse(
  bookmarks: BibleBookmark[],
  bookSlug: string,
  chapter: number,
): Map<number, number> {
  const m = new Map<number, number>();
  for (const b of bookmarks) {
    if (b.deletedAt) continue;
    if (b.anchor.bookSlug !== bookSlug || b.anchor.chapter !== chapter) continue;
    for (const v of b.anchor.verses) m.set(v, (m.get(v) ?? 0) + 1);
  }
  return m;
}

/**
 * All live notes whose passage covers `verse`, newest-first (D4). A
 * single-verse note and a passage note that includes the verse both
 * appear here; the UI labels each by its own passage reference so
 * co-terminal / overlapping passages are never ambiguous (BLOCKER 2
 * resolution). Sorted `createdAt` descending so the freshest reflection
 * sits just below the add composer.
 */
export function notesCoveringVerse(
  bookmarks: BibleBookmark[],
  bookSlug: string,
  chapter: number,
  verse: number,
): BibleBookmark[] {
  return bookmarks
    .filter(
      (b) =>
        !b.deletedAt &&
        b.anchor.bookSlug === bookSlug &&
        b.anchor.chapter === chapter &&
        b.anchor.verses.includes(verse),
    )
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
}

/**
 * In-list search for the Scripture Journal (Phase 4, §6). Multi-word AND over
 * reference + bookName + note + frozen verse text, accent-folded via the
 * canonical normalizer (Bible-search Plan 01) so es/pt accents fold. This is
 * search WITHIN the user's own bookmarks (private-by-default posture, feature
 * plan §6): bookmarks have no outbound surface, so this is never a discovery
 * surface. No ranking; callers keep their own sort (recency / canonical).
 */
export function searchBookmarks(
  query: string,
  bookmarks: BibleBookmark[],
): BibleBookmark[] {
  const words = normalizeForSearch(query).split(' ').filter(Boolean);
  if (words.length === 0) return bookmarks;
  return bookmarks.filter((b) => {
    const hay = normalizeForSearch(
      `${b.anchor.reference} ${b.anchor.bookName} ${b.note} ${b.anchor.text}`,
    );
    return words.every((w) => hay.includes(w));
  });
}
